import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle } from "lucide-react";
import { useTr } from "@/i18n/translator";

export default function BillingSuccess() {
  const tr = useTr();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [planId, setPlanId] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("missing");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        setPlanId(data.plan_id);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts >= 10) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setStatus("error");
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#F4F4F0] text-[#0A0A0A] flex items-center justify-center p-6" data-testid="billing-success-page">
      <div className="bg-white border border-[#0A0A0A] brutal-shadow max-w-md w-full p-8">
        <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("CIA MARKETING® · OPERACIÓN")}</div>
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-[#E53935] mt-6" strokeWidth={1.5} />
            <h1 className="font-display text-4xl mt-4">Pago confirmado.</h1>
            {(planId === "brand_service" || planId === "brand_service_ecom") ? (
              <>
                <p className="mt-3 font-mono-data text-sm text-[#5C5C5C]">
                  {tr("¡Bienvenido al")} <b className="text-[#0A0A0A]">{tr("Servicio de Branding IA")}</b>!
                  {tr(" Ahora vamos a construir tu marca completa con IA: logo, paleta, tipografía, slogan y landing page.")}
                </p>
                <button onClick={() => nav("/dashboard/brand-builder")} className="mt-8 w-full bg-[#E53935] text-white px-4 py-3 font-mono-mark text-xs hover-brutal border border-[#E53935]" data-testid="billing-success-cta">
                  EMPEZAR BRAND BUILDER →
                </button>
                <button onClick={() => nav("/brand-service")} className="mt-2 w-full bg-white text-[#0A0A0A] px-4 py-3 font-mono-mark text-xs border border-[#0A0A0A]">
                  VER MIS PEDIDOS
                </button>
              </>
            ) : (
              <>
                <p className="mt-3 font-mono-data text-sm text-[#5C5C5C]">
                  {tr("Tu plan")} <b className="text-[#0A0A0A] uppercase">{planId}</b> {tr("quedó activo. Bienvenido al nivel superior.")}
                </p>
                <button onClick={() => nav("/dashboard")} className="mt-8 w-full bg-[#0A0A0A] text-white px-4 py-3 font-mono-mark text-xs hover-brutal border border-[#0A0A0A]" data-testid="billing-success-cta">
                  ENTRAR AL DASHBOARD
                </button>
              </>
            )}
          </>
        )}
        {status === "checking" && (
          <>
            <div className="mt-6 h-14 w-14 border-4 border-[#0A0A0A] border-t-[#E53935] rounded-full animate-spin" />
            <h1 className="font-display text-4xl mt-4">Verificando pago…</h1>
            <p className="mt-3 font-mono-data text-sm text-[#5C5C5C]">Esto tarda unos segundos.</p>
          </>
        )}
        {(status === "timeout" || status === "error" || status === "expired" || status === "missing") && (
          <>
            <XCircle className="w-14 h-14 text-[#E53935] mt-6" strokeWidth={1.5} />
            <h1 className="font-display text-4xl mt-4">{tr("Algo falló.")}</h1>
            <p className="mt-3 font-mono-data text-sm text-[#5C5C5C]">
              {status === "missing" ? "No se recibió session_id." : "No pudimos confirmar el pago. Si te cobraron, escríbenos."}
            </p>
            <div className="mt-8 flex gap-3">
              <Link to="/pricing" className="flex-1 text-center bg-[#0A0A0A] text-white px-4 py-3 font-mono-mark text-xs border border-[#0A0A0A]">REINTENTAR</Link>
              <Link to="/dashboard" className="flex-1 text-center bg-white text-[#0A0A0A] px-4 py-3 font-mono-mark text-xs border border-[#0A0A0A]">DASHBOARD</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
